import { RotationMode, ZIndexes } from "../constants";
import { type TrailKind } from "./bloodTrails";
import { DefinitionType, ObjectDefinitions, type ObjectDefinition } from "../utils/objectDefinitions";
import { HealingItems } from "./items/healingItems";

export interface DecalDefinition extends ObjectDefinition {
    readonly defType: DefinitionType.Decal
    readonly image?: string
    /**
     * @default {1}
     */
    readonly scale?: number
    readonly rotationMode: RotationMode
    readonly zIndex?: ZIndexes
    readonly alpha?: number
    /**
     * the battlefield's old gore (0.86, owner request, 20 October 2026: "in the map, add gory effects such as blood
     * splatters in random areas, body parts,etc"), placed when the map is made (server/src/data/mapGore.ts). Each
     * player's gore setting decides whether it's drawn (client objects/decal.ts): blood unless it's off, remains (body
     * parts, bones) on "extreme" only, as the runtime gore's dismemberment is
     */
    readonly gore?: "blood" | "remains"
    /**
     * a blood trail's mark (1.06: common/src/definitions/bloodTrails.ts), made by the server as a player bleeds or walks
     * in bloody boots, and sent with its age: drawn white and tinted as it dries (client managers/bloodTrails.ts), always
     * shown (they're the others' to follow; with gore off, in the colour of mud), gone when its kind's life is over
     */
    readonly trail?: TrailKind
}

/** the gore's pictures (client/public/img/game/decals/gore/, drawn by tools/gore/gore_decals.py) */
export const GORE_BLOOD = [
    "gore_pool_1", "gore_pool_2", "gore_splat_1", "gore_splat_2", "gore_splat_3", "gore_splat_4", "gore_spray",
    "gore_drips_1", "gore_drips_2", "gore_drag_1", "gore_drag_2", "gore_bootprints", "gore_stain_1", "gore_stain_2"
] as const;
export const GORE_REMAINS = [
    "gore_arm", "gore_leg", "gore_hand", "gore_boot", "gore_chunks", "gore_guts", "gore_skull", "gore_bones", "gore_ribcage", "gore_helmet"
] as const;
export type GoreDecal = typeof GORE_BLOOD[number] | typeof GORE_REMAINS[number];

/** the blood trails' marks (1.06: client/public/img/game/decals/trail/, drawn by tools/gore/gore_decals.py), by kind */
export const TRAIL_DROPS = ["trail_drop_1", "trail_drop_2", "trail_drop_3", "trail_drop_4"] as const;
/** a drop that fell from someone moving: a streak along their way (its picture drawn pointing right) */
export const TRAIL_STREAKS = ["trail_streak_1", "trail_streak_2", "trail_streak_3"] as const;
/** two strides of bloody boot prints, along the way walked (pointing right, the picture's length BloodTrail.printStep) */
export const TRAIL_PRINTS = ["trail_prints_1", "trail_prints_2", "trail_prints_3"] as const;
/** where a bleeding player stood: a pool others step in */
export const TRAIL_PUDDLES = ["trail_puddle_1", "trail_puddle_2"] as const;
export type TrailDecal = typeof TRAIL_DROPS[number] | typeof TRAIL_STREAKS[number] | typeof TRAIL_PRINTS[number] | typeof TRAIL_PUDDLES[number];

/**
 * whether a gore decal is drawn at a player's gore setting (cv_gore: off, normal, extreme), as the runtime gore is: no
 * blood at all when it's off, and the remains (body parts, bones) at "extreme" only, as the runtime gore's dismemberment
 */
export function goreDrawn(kind: "blood" | "remains", level: string): boolean {
    if (level === "off") return false;
    return kind === "blood" || level === "extreme";
}

export const Decals = new ObjectDefinitions<DecalDefinition>([
    {
        idString: "explosion_decal",
        name: "Explosion Decal",
        defType: DefinitionType.Decal,
        rotationMode: RotationMode.Full
    },
    {
        idString: "frag_explosion_decal",
        name: "Frag Explosion Decal",
        defType: DefinitionType.Decal,
        rotationMode: RotationMode.Full
    },
    {
        idString: "smoke_explosion_decal",
        name: "Smoke Explosion Decal",
        defType: DefinitionType.Decal,
        rotationMode: RotationMode.Full
    },
    {
        idString: "seed_decal",
        name: "Seed Decal",
        defType: DefinitionType.Decal,
        rotationMode: RotationMode.Full
    },
    {
        idString: "seed_explosion_decal",
        name: "Seed Explosion Decal",
        defType: DefinitionType.Decal,
        rotationMode: RotationMode.Full
    },
    {
        idString: "used_flare_decal",
        name: "Used Flare Decal",
        defType: DefinitionType.Decal,
        rotationMode: RotationMode.Full
    },
    {
        idString: "used_flare_decal_halloween",
        name: "Used Flare Decal",
        defType: DefinitionType.Decal,
        rotationMode: RotationMode.Full
    },
    {
        idString: "seed_explosion_decal_infected",
        name: "Infected Seed Explosion Decal",
        defType: DefinitionType.Decal,
        rotationMode: RotationMode.Full
    },
    {
        idString: "vomit_pool",
        name: "Vomit Pool",
        defType: DefinitionType.Decal,
        rotationMode: RotationMode.Full
    },
    {
        idString: "vomit_pool_wtr",
        name: "Vomit Pool",
        defType: DefinitionType.Decal,
        rotationMode: RotationMode.Full,
        alpha: 0.5,
        zIndex: ZIndexes.Ground,
        image: "vomit_pool"
    },
    ...HealingItems.definitions.map(healingItem => {
        return {
            idString: `${healingItem.idString}_residue`,
            name: `${healingItem.name} Residue`,
            defType: DefinitionType.Decal,
            rotationMode: RotationMode.Full
        } as DecalDefinition;
    }),
    // (last, so every decal before keeps its number on the wire)
    ...GORE_BLOOD.map((idString): DecalDefinition => ({
        idString,
        name: "Blood",
        defType: DefinitionType.Decal,
        rotationMode: RotationMode.Full,
        gore: "blood"
    })),
    ...GORE_REMAINS.map((idString): DecalDefinition => ({
        idString,
        name: "Remains",
        defType: DefinitionType.Decal,
        rotationMode: RotationMode.Full,
        gore: "remains"
    })),
    // the blood trails' marks (1.06: after the gore, every decal before keeping its number on the wire)
    ...([[TRAIL_DROPS, "drop"], [TRAIL_STREAKS, "drop"], [TRAIL_PRINTS, "print"], [TRAIL_PUDDLES, "puddle"]] as const).flatMap(
        ([ids, trail]) => ids.map((idString): DecalDefinition => ({
            idString,
            name: "Blood Trail",
            defType: DefinitionType.Decal,
            rotationMode: RotationMode.Full,
            trail
        }))
    )
]);
